import {
  ColDef,
  GridReadyEvent,
  ValueFormatterParams,
  IServerSideGetRowsParams,
  ICellRendererParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  dateFilterModel,
  dateFilterParams,
} from "../../../../utils/Formatter/AgGridFilter";
import { formatDate, formatDateTimeWithSeconds } from "../../../../utils/Date";
import AgGrid from "../../../UI/AgGrid";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { GOODS_TRANSFER_REPORT_VIEW } from "../../../../services/AgGrid/Report/InventoryReportAggrid";
import CustomizedStatus from "../../../Custom/CustomizedStatus";
import { statusValueFormatter } from "../../../../utils/Formatter/Global";

interface Props {
  gridRef: RefObject<AgGridReact>;
}

const GoodsTransferReportTable = ({ gridRef }: Props) => {
  const { t } = useTranslation();

  const columnDefs: ColDef[] = [
    {
      field: "created_date",
      headerName: t("date.created_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "posted_date",
      headerName: t("date.transfered_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "unique_id",
      headerName: t("sentence.unique_id"),
      filter: "agTextColumnFilter",
    },
    {
      field: "source_branch_id",
      headerName: t("branch.unique_id") + "ต้นทาง",
      filter: "agNumberColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "source_branch_unique_id",
      headerName: t("branch.unique_id") + "ต้นทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "source_branch_name",
      headerName: t("branch.index") + "ต้นทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "source_warehouse_id",
      headerName: t("inventory.warehouseId") + "ต้นทาง",
      filter: "agTextColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "source_warehouse_unique_id",
      headerName: t("inventory.warehouseId") + "ต้นทาง",
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "source_warehouse_name",
      headerName: t("inventory.warehouse") + "ต้นทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "destination_branch_id",
      headerName: t("branch.unique_id") + "ปลายทาง",
      filter: "agNumberColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "destination_branch_unique_id",
      headerName: t("branch.unique_id") + "ปลายทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "destination_branch_name",
      headerName: t("branch.index") + "ปลายทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "destination_warehouse_id",
      headerName: t("inventory.warehouseId") + "ปลายทาง",
      filter: "agTextColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "destination_warehouse_unique_id",
      headerName: t("inventory.warehouseId") + "ปลายทาง",
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "destination_warehouse_name",
      headerName: t("inventory.warehouse") + "ปลายทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "remark",
      headerName: t("sentence.remark") + "หลัก",
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "aggrid_status",
      headerName: t("inventory.goods_transfer.table.column.status"),
      filter: "agSetColumnFilter",
      cellRenderer: (params: ICellRendererParams) => (
        <CustomizedStatus status={params.value} />
      ),
      filterParams: {
        values: [
          "draft",
          "wait_approve",
          "not_approved",
          "wait_transfer",
          "in_transit",
          "finished",
          "cancelled",
        ],
        valueFormatter: (params: ValueFormatterParams) =>
          statusValueFormatter(params.value),
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
    {
      field: "source_scanned_date",
      headerName: "วันที่สแกน (ต้นทาง)",
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateTimeWithSeconds(params.value),
    },
    {
      field: "item_name",
      headerName: "รหัสสินค้า-ชื่อสินค้า",
      filter: "agTextColumnFilter",
    },
    {
      field: "qty",
      headerName: "จำนวน",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "uom",
      headerName: t("inventory.unit"),
      filter: "agTextColumnFilter",
    },
    {
      field: "serial_no",
      headerName: "Batch/SN",
      filter: "agTextColumnFilter",
    },
    {
      field: "barcode",
      headerName: "Barcode",
      filter: "agTextColumnFilter",
    },
    {
      field: "source_bin_location_id",
      headerName: t("inventory.locationId") + "ต้นทาง",
      filter: "agNumberColumnFilter",
      hide: true,
    },
    {
      field: "source_bin_location_name",
      headerName: t("inventory.location") + "ต้นทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "source_scanned_by_name",
      headerName: "ผู้สแกนต้นทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "destination_bin_location_id",
      headerName: t("inventory.locationId") + "ปลายทาง",
      filter: "agNumberColumnFilter",
      hide: true,
    },
    {
      field: "destination_bin_location_name",
      headerName: t("inventory.location") + "ปลายทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "destination_scanned_status",
      headerName: "สถานะการสแกน",
      filter: "agSetColumnFilter",
      filterParams: {
        values: [1, 0],
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? t("status.is_scanned") : t("status.is_active"),
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <CustomizedStatus
            status={params.value ? "is_scanned" : "is_active"}
          />
        );
      },
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      field: "destination_scanned_by_name",
      headerName: "ผู้สแกนปลายทาง",
      filter: "agTextColumnFilter",
    },
    {
      field: "destination_scanned_date",
      headerName: "วันที่สแกน (ปลายทาง)",
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateTimeWithSeconds(params.value),
    },
    {
      field: "item_remark",
      headerName: t("sentence.remark") + "รายการ",
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "lot_date",
      headerName: t("reports.lot_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "manufactured_date",
      headerName: t("date.manufactured_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      hide: true,
    },
    {
      field: "expired_date",
      headerName: t("date.expired_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      hide: true,
    },
    {
      field: "created_by_name",
      headerName: t("sentence.created_by"),
      filter: "agTextColumnFilter",
    },
    {
      field: "last_updated_date",
      headerName: t("date.last_updated_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      hide: true,
    },
  ];

  const graphQLClient: GraphQLClient = createGraphQLClientWithMiddleware("wms");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      const {
        lot_date,
        manufactured_date,
        expired_date,
        created_date,
        posted_date,
        source_scanned_date,
        destination_scanned_date,
        destination_scanned_status,
        last_updated_date,
      } = filterModel;

      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        posted_date: dateFilterModel(posted_date),
        source_scanned_date: dateFilterModel(source_scanned_date),
        destination_scanned_date: dateFilterModel(destination_scanned_date),
        lot_date: dateFilterModel(lot_date),
        manufactured_date: dateFilterModel(manufactured_date),
        expired_date: dateFilterModel(expired_date),
        destination_scanned_status: destination_scanned_status && {
          filterType: "setBoolean",
          values: destination_scanned_status.values.map(
            (v: string) => v === "1"
          ),
        },
        last_updated_date: dateFilterModel(last_updated_date),
      };

      try {
        const { GoodsTransferReportView } = await graphQLClient.request(
          GOODS_TRANSFER_REPORT_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          }
        );
        params.success({
          rowData: GoodsTransferReportView.results,
          rowCount: GoodsTransferReportView.count,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onGridReady = (params: GridReadyEvent) => {
    params.api.setServerSideDatasource(datasource);
  };

  return (
    <AgGrid
      height={665}
      ref={gridRef}
      columnDefs={columnDefs}
      onGridReady={onGridReady}
    />
  );
};

export default GoodsTransferReportTable;
